import { FloorplanImageImportFieldsFragment } from "../../gql/graphql";
import { isFloorplanImportSymbolInsertable } from "./importedSymbolGeometry";

type ImportDraft = NonNullable<FloorplanImageImportFieldsFragment["draft"]>;

const hasNoVisibleMeasurementEvidence = (draft: ImportDraft) =>
  draft.issues.some((issue) => {
    const code = issue.code.toUpperCase();
    const message = issue.message.toLowerCase();
    return (
      code === "NO_VISIBLE_MEASUREMENTS" ||
      /no (clear |visible )?(dimension|measurement)/.test(message) ||
      /(dimension|measurement).*(not visible|not present)/.test(message)
    );
  });

/**
 * Default exclusions when inserting without an interactive review step.
 * Unmatched symbols stay off; measurements stay off when the draft reports no
 * visible dimension evidence. Room labels are included by default.
 */
export const getDefaultExcludedRefs = (draft: ImportDraft) => {
  const excluded = new Set<string>();
  draft.symbols.forEach((symbol) => {
    if (!isFloorplanImportSymbolInsertable(symbol)) excluded.add(symbol.ref);
  });
  if (hasNoVisibleMeasurementEvidence(draft)) {
    draft.measurements.forEach((measurement) => excluded.add(measurement.ref));
  }
  return excluded;
};

/** Resolve exclusions/overrides from persisted review state, else defaults. */
export const resolveFloorplanImportReviewSelection = (
  job: FloorplanImageImportFieldsFragment,
) => {
  if (!job.draft) {
    return {
      excludedRefs: new Set<string>(),
      symbolOverrides: new Map<
        string,
        { libraryKey: string; rotation: number }
      >(),
    };
  }
  const excludedRefs = job.reviewState
    ? new Set(job.reviewState.excludedEntityIds)
    : getDefaultExcludedRefs(job.draft);
  const symbolOverrides = new Map(
    (job.reviewState?.symbolOverrides ?? []).map((override) => [
      override.ref,
      { libraryKey: override.libraryKey, rotation: override.rotation },
    ]),
  );
  return { excludedRefs, symbolOverrides };
};
